import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiCalendar,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiEye,
  FiMapPin,
  FiMoreHorizontal,
  FiSearch,
} from "react-icons/fi";
import ComplaintApprovedIcon from "../icons/ComplaintApprovedIcon";
import ComplaintRejectedIcon from "../icons/ComplaintRejectedIcon";
import AdminComplaintController from "../controllers/AdminComplaintController";
import { userService } from "../services/userService";
import "../styles/ComplaintCenter.css";
import "../styles/Modals.css";
import { translateLabel, useTranslationSnapshot } from "./../translations/runtime";


const STATUS_OPTIONS = ["All Status", "Active", "Pending"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const formatDateInput = (date) => {
  if (!date) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${date.getFullYear()}`;
};

const formatRangeLabel = ({ from, to }) => {
  if (from && to) return `${formatDateInput(from)} - ${formatDateInput(to)}`;
  if (from) return formatDateInput(from);
  if (to) return formatDateInput(to);
  return "Date Range";
};

const sameDay = (leftDate, rightDate) =>
  Boolean(
    leftDate &&
      rightDate &&
      leftDate.getFullYear() === rightDate.getFullYear() &&
      leftDate.getMonth() === rightDate.getMonth() &&
      leftDate.getDate() === rightDate.getDate(),
  );

const isBetweenDates = (date, from, to) => {
  if (!date || !from || !to) return false;
  const time = date.setHours(0, 0, 0, 0);
  const fromTime = new Date(from).setHours(0, 0, 0, 0);
  const toTime = new Date(to).setHours(0, 0, 0, 0);
  return time > fromTime && time < toTime;
};

const buildCalendarCells = (visibleMonth) => {
  const year = visibleMonth.getFullYear();
  const month = visibleMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const startDate = new Date(firstDay);
  startDate.setDate(firstDay.getDate() - firstDay.getDay());

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + index);

    return {
      date,
      outside: date.getMonth() !== month,
    };
  });
};

const formatLongDate = (date) =>
  date
    ? date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";

const getStoredToken = () => {
  const storedUser = userService.getJwt();
  if (typeof storedUser === "string") return storedUser;

  return (
    storedUser?.jwt ||
    storedUser?.token ||
    storedUser?.accessToken ||
    storedUser?.access_token ||
    ""
  );
};

const unwrapPayload = (response) =>
  response?.data?.data ?? response?.data?.result ?? response?.data ?? response ?? {};

const unwrapList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.complaints)) return payload.complaints;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const formatApiDate = (date) => {
  if (!date) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const formatDisplayDate = (value) => {
  const date = value?.time ? new Date(value.time) : new Date(value || "");
  if (Number.isNaN(date.getTime())) return value || "-";

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const mapComplaint = (complaint = {}) => {
  const customer =
    complaint.customer ||
    complaint.recipient ||
    complaint.user ||
    complaint.customerDetails ||
    {};
  const order = complaint.order || complaint.orderDetails || {};
  const vendor = complaint.vendor || complaint.company || complaint.store || {};
  const driver = complaint.driver || complaint.delivery || {};
  const id =
    complaint.complaintId ||
    complaint.id ||
    complaint.requestId ||
    complaint.referenceId ||
    "";

  return {
    raw: complaint,
    complaintId: id,
    id: complaint.complaintNumber || complaint.ticketNumber || complaint.referenceId || id || "-",
    name:
      complaint.customerName ||
      complaint.recipientName ||
      customer.name ||
      customer.fullName ||
      "-",
    phone:
      complaint.customerPhone ||
      complaint.recipientPhone ||
      customer.phoneNumber ||
      customer.phone ||
      "-",
    orderId: complaint.orderId || order.orderId || order.id || "-",
    amount:
      complaint.amount ||
      complaint.refundAmount ||
      complaint.totalRefund ||
      order.total ||
      "$0.00",
    reason: complaint.reason || complaint.complaintReason || complaint.title || "-",
    status: complaint.status || complaint.complaintStatus || "Pending",
    created: formatDisplayDate(complaint.createdAt || complaint.submittedAt || complaint.createdDate),
    message: complaint.message || complaint.customerMessage || complaint.description || "",
    vendorName: complaint.vendorName || vendor.name || vendor.companyName || "Pizza House",
    vendorPhone: complaint.vendorPhone || vendor.phone || vendor.phoneNumber || "-",
    driverName: complaint.driverName || driver.name || "-",
    driverPhone: complaint.driverPhone || driver.phone || driver.phoneNumber || "-",
    pickupAddress:
      complaint.pickupAddress ||
      vendor.address ||
      "Main Street, Bednayel, Bekaa",
    deliveryAddress:
      complaint.deliveryAddress ||
      customer.address ||
      order.deliveryAddress ||
      "Bednayel, Bekaa",
    imageUrl:
      complaint.imageUrl ||
      complaint.attachmentUrl ||
      complaint.photoUrl ||
      complaint.customerImageUrl ||
      "",
    submittedOn: formatDisplayDate(complaint.submittedAt || complaint.createdAt),
  };
};

export default function ComplaintCenterView() {
  useTranslationSnapshot();
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [complaints, setComplaints] = useState([]);
  const [complaintsLoading, setComplaintsLoading] = useState(false);
  const [complaintsError, setComplaintsError] = useState("");
  const [detailsLoadingId, setDetailsLoadingId] = useState("");
  const [voucherOpen, setVoucherOpen] = useState(true);
  const [complaintModal, setComplaintModal] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState("");
  const [expiryDate, setExpiryDate] = useState(new Date(2026, 8, 30));
  const [expiryPickerOpen, setExpiryPickerOpen] = useState(false);
  const [expiryMonth, setExpiryMonth] = useState(new Date(2026, 8, 1));
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All Status");
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isDateOpen, setIsDateOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(new Date(2026, 0, 1));
  const [dateRange, setDateRange] = useState({
    from: new Date(2026, 0, 2),
    to: new Date(2026, 0, 11),
  });
  const [draftRange, setDraftRange] = useState(dateRange);
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    status: "",
    fromDate: formatApiDate(new Date(2026, 0, 2)),
    toDate: formatApiDate(new Date(2026, 0, 11)),
  });

  const calendarCells = useMemo(() => {
    return buildCalendarCells(visibleMonth);
  }, [visibleMonth]);

  const expiryCalendarCells = useMemo(() => {
    return buildCalendarCells(expiryMonth);
  }, [expiryMonth]);

  const visibleMonthLabel = visibleMonth.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });
  const expiryMonthLabel = expiryMonth.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  const loadComplaints = useCallback(async () => {
    setComplaintsLoading(true);
    setComplaintsError("");

    try {
      const response = await AdminComplaintController.list({
        Authorization: getStoredToken(),
        search: appliedFilters.search,
        status: appliedFilters.status,
        fromDate: appliedFilters.fromDate,
        toDate: appliedFilters.toDate,
      });
      const payload = unwrapPayload(response);
      const rows = unwrapList(payload).map(mapComplaint);
      setComplaints(rows);
    } catch (error) {
      console.error("[Admin complaints] list failed", error);
      setComplaintsError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to load complaints.",
      );
      setComplaints([]);
    } finally {
      setComplaintsLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    loadComplaints();
  }, [loadComplaints]);

  const filteredComplaints = useMemo(() => {
    return complaints;
  }, [complaints]);

  const openComplaintDetails = async (complaint) => {
    if (!complaint.complaintId) {
      setSelectedComplaint(complaint);
      return;
    }

    setDetailsLoadingId(complaint.complaintId);
    try {
      const response = await AdminComplaintController.details({
        Authorization: getStoredToken(),
        complaintId: complaint.complaintId,
      });
      setSelectedComplaint(mapComplaint({ ...complaint.raw, ...unwrapPayload(response) }));
    } catch (error) {
      console.error("[Admin complaints] details failed", error);
      setSelectedComplaint(complaint);
    } finally {
      setDetailsLoadingId("");
    }
  };

  const clearFilters = () => {
    setQuery("");
    setStatus("All Status");
    setDateRange({ from: null, to: null });
    setDraftRange({ from: null, to: null });
    setIsStatusOpen(false);
    setIsDateOpen(false);
    setAppliedFilters({
      search: "",
      status: "",
      fromDate: "",
      toDate: "",
    });
  };

  const applyFilters = () => {
    setAppliedFilters({
      search: query.trim(),
      status: status === "All Status" ? "" : status,
      fromDate: formatApiDate(dateRange.from),
      toDate: formatApiDate(dateRange.to),
    });
  };

  const approveComplaint = async () => {
    setActionError("");
    if (!selectedComplaint?.complaintId) {
      setComplaintModal("approved");
      return;
    }

    setActionLoading(true);
    try {
      await AdminComplaintController.approve({
        Authorization: getStoredToken(),
        complaintId: selectedComplaint.complaintId,
      });
      setComplaintModal("approved");
      await loadComplaints();
    } catch (error) {
      console.error("[Admin complaints] approve failed", error);
      setActionError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to approve complaint.",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const rejectComplaint = async () => {
    setActionError("");
    if (!selectedComplaint?.complaintId) {
      setComplaintModal(null);
      return;
    }

    setActionLoading(true);
    try {
      await AdminComplaintController.reject({
        Authorization: getStoredToken(),
        complaintId: selectedComplaint.complaintId,
        rejectionReason,
      });
      setComplaintModal(null);
      setSelectedComplaint(null);
      setRejectionReason("");
      await loadComplaints();
    } catch (error) {
      console.error("[Admin complaints] reject failed", error);
      setActionError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to reject complaint.",
      );
    } finally {
      setActionLoading(false);
    }
  };

  const moveMonth = (direction) => {
    setVisibleMonth(
      (currentMonth) =>
        new Date(
          currentMonth.getFullYear(),
          currentMonth.getMonth() + direction,
          1,
        ),
    );
  };

  const moveExpiryMonth = (direction) => {
    setExpiryMonth(
      (currentMonth) =>
        new Date(
          currentMonth.getFullYear(),
          currentMonth.getMonth() + direction,
          1,
        ),
    );
  };

  const selectDate = (date) => {
    setDraftRange((currentRange) => {
      if (!currentRange.from || currentRange.to || date < currentRange.from) {
        return { from: date, to: null };
      }

      return { from: currentRange.from, to: date };
    });
  };

  const clearDateRange = () => {
    setDraftRange({ from: null, to: null });
    setDateRange({ from: null, to: null });
    setIsDateOpen(false);
  };

  const applyDateRange = () => {
    setDateRange(draftRange);
    setIsDateOpen(false);
  };

  if (selectedComplaint) {
    return (
      <div className="complaint-center-page complaint-detail-page">
        <div className="complaint-center-header">
          <div className="complaint-center-heading">
            <button
              type="button"
              className="complaint-detail-back"
              onClick={() => setSelectedComplaint(null)}
            >
              <FiChevronLeft className="navigation-direction-icon" size={17} />
            </button>
            <div>
              <h1>{translateLabel("txt_admin_complaint_issue_center", "Complaint & Issue Center")}</h1>
              <p>{translateLabel("txt_admin_manage_issues_related_to_discovery_browsing_and_fulfillment", "Manage issues related to discovery, browsing, and fulfillment.")}</p>
            </div>
          </div>
        </div>

        <div className="complaint-detail-grid">
          <div className="complaint-detail-main">
            <section className="complaint-detail-section">
              <div className="complaint-detail-section-title">
                <span>01</span>
                <strong>{translateLabel("txt_admin_recipient", "Recipient")}</strong>
              </div>
              <p className="complaint-detail-help">
                Who is this refund or adjustment for?
              </p>

              <div className="complaint-detail-form-grid">
                <label>
                  <span>{translateLabel("txt_admin_recipient_name", "Recipient Name")}</span>
                  <input value={selectedComplaint.name} readOnly />
                </label>
                <label>
                  <span>{translateLabel("input_phone_number", "Phone Number")}</span>
                  <input value={selectedComplaint.phone} readOnly />
                </label>
                <label>
                  <span>{translateLabel("input_vendor_name", "Vendor Name")}</span>
                  <input value={selectedComplaint.vendorName} readOnly />
                </label>
                <label>
                  <span>{translateLabel("txt_admin_vendor_phone_number", "Vendor Phone Number")}</span>
                  <input value={selectedComplaint.vendorPhone} readOnly />
                </label>
                <label>
                  <span>{translateLabel("txt_common_driver_name", "Driver Name")}</span>
                  <input value={selectedComplaint.driverName} readOnly />
                </label>
                <label>
                  <span>{translateLabel("txt_admin_driver_phone_number", "Driver Phone Number")}</span>
                  <input value={selectedComplaint.driverPhone} readOnly />
                </label>
              </div>
            </section>

            <section className="complaint-detail-section">
              <div className="complaint-detail-section-title">
                <span>02</span>
                <strong>{translateLabel("txt_common_delivery_location", "Delivery Location")}</strong>
              </div>
              <div className="complaint-location-grid">
                <div>
                  <span>{translateLabel("txt_admin_from", "From")}</span>
                  <div className="complaint-location-card">
                    <FiMapPin size={16} />
                    <div>
                      <strong>{selectedComplaint.vendorName}</strong>
                      <small>{selectedComplaint.pickupAddress}</small>
                    </div>
                  </div>
                </div>
                <div>
                  <span>{translateLabel("txt_time_to", "To")}</span>
                  <div className="complaint-location-card">
                    <FiMapPin size={16} />
                    <div>
                      <strong>{selectedComplaint.name}</strong>
                      <small>{selectedComplaint.deliveryAddress}</small>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="complaint-detail-section">
              <div className="complaint-detail-section-title">
                <span>03</span>
                <strong>{translateLabel("txt_admin_customer_s_message", "Customer's Message")}</strong>
              </div>
              <div className="complaint-message">
                "{selectedComplaint.message || selectedComplaint.reason}"
              </div>

              <div className="complaint-upload">
                <strong>{translateLabel("txt_admin_uploaded_photo_from_customer", "Uploaded Photo from Customer")}</strong>
                <div className="complaint-upload-row">
                  {selectedComplaint.imageUrl ? (
                    <img
                      className="complaint-upload-image"
                      src={selectedComplaint.imageUrl}
                      alt={translateLabel("txt_admin_complaint_attachment", "Complaint attachment")}
                    />
                  ) : (
                    <div className="complaint-upload-image" />
                  )}
                  <div>
                    <span>Photo_20260522_1015.jpeg</span>
                    <small>Uploaded on May 22,2026 - 10:20 AM</small>
                  </div>
                  <button type="button" aria-label={translateLabel("btn_download", "Download")}>
                    <FiDownload size={16} />
                  </button>
                </div>
              </div>
            </section>

            <section className="complaint-detail-section">
              <div className="complaint-detail-section-title">
                <span>04</span>
                <strong>{translateLabel("txt_admin_feedback_section", "Feedback Section")}</strong>
              </div>
              <label className="complaint-feedback">
                <span>{translateLabel("txt_admin_internal_notes_or_feedback_optional", "Internal notes or feedback (optional)")}</span>
                <textarea placeholder={translateLabel("txt_admin_add_a_note_feedback_or_comment", "Add a note, feedback, or comment...")} />
                <small>0/500</small>
              </label>
            </section>

            <section className="complaint-voucher">
              <div className="complaint-detail-section-title">
                <span>05</span>
                <strong>{translateLabel("txt_admin_create_voucher", "Create Voucher")}</strong>
                <button
                  type="button"
                  className={`complaint-toggle ${voucherOpen ? "is-on" : ""}`}
                  onClick={() => setVoucherOpen((current) => !current)}
                  aria-pressed={voucherOpen}
                >
                  <span />
                </button>
              </div>
              {voucherOpen && (
                <>
                  <small>{translateLabel("txt_admin_create_a_voucher_for_this_customer", "Create a voucher for this customer.")}</small>
                  <div className="complaint-voucher-grid">
                    <label>
                      <span>{translateLabel("txt_admin_voucher_name", "Voucher Name")}</span>
                      <input placeholder={translateLabel("txt_admin_e_g_service_recovery", "e.g. Service Recovery")} />
                    </label>
                    <label>
                      <span>{translateLabel("txt_admin_voucher_value_type", "Voucher Value Type")}</span>
                      <select defaultValue="USD">
                        <option>{translateLabel("txt_currency_usd", "USD")}</option>
                        <option>{translateLabel("txt_admin_percentage", "Percentage")}</option>
                        <option>LBP</option>
                      </select>
                    </label>
                <label>
                  <span>{translateLabel("txt_admin_expiry_date", "Expiry Date")}</span>
                  <div className="complaint-expiry-picker">
                    <button
                      type="button"
                      className="complaint-expiry-trigger"
                      onClick={() => setExpiryPickerOpen((current) => !current)}
                    >
                      <span>{formatLongDate(expiryDate)}</span>
                      <FiCalendar size={24} />
                    </button>

                    {expiryPickerOpen && (
                      <div className="complaint-expiry-popover">
                        <div className="complaint-calendar-month">
                          <button type="button" onClick={() => moveExpiryMonth(-1)}>
                            <FiChevronLeft className="navigation-direction-icon" size={15} />
                          </button>
                          <span>{expiryMonthLabel}</span>
                          <button type="button" onClick={() => moveExpiryMonth(1)}>
                            <FiChevronRight className="navigation-direction-icon" size={15} />
                          </button>
                        </div>
                        <div className="complaint-calendar-grid">
                          {WEEKDAYS.map((day) => (
                            <div className="complaint-calendar-weekday" key={day}>
                              {day}
                            </div>
                          ))}
                          {expiryCalendarCells.map(({ date, outside }) => (
                            <button
                              type="button"
                              key={date.toISOString()}
                              className={[
                                "complaint-calendar-day",
                                outside ? "outside" : "",
                                sameDay(date, expiryDate) ? "selected" : "",
                              ]
                                .filter(Boolean)
                                .join(" ")}
                              onClick={() => {
                                setExpiryDate(date);
                                setExpiryPickerOpen(false);
                              }}
                            >
                              {date.getDate()}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </label>
                    <label>
                      <span>{translateLabel("txt_admin_voucher_value", "Voucher Value")}</span>
                      <input placeholder="$18.50" />
                    </label>
                  </div>
                </>
              )}
            </section>

            <div className="complaint-approval-note">
              <strong>{translateLabel("txt_admin_requires_vendor_approval", "Requires vendor approval")}</strong>
              <span>
                {actionError ||
                  translateLabel("txt_admin_please_review_the_details_and_accept_or_reject_this_refund_request", "Please review the details and accept or reject this refund request.")}
              </span>
            </div>

            <div className="complaint-detail-actions">
              <button
                type="button"
                className="modal-btn-send"
                onClick={approveComplaint}
                disabled={actionLoading}
              >
                {actionLoading ? translateLabel("txt_end_user_saving", "Saving...") : translateLabel("btn_accept", "Accept")}
              </button>
              <button
                type="button"
                className="modal-btn-reject"
                onClick={() => {
                  setActionError("");
                  setComplaintModal("rejected");
                }}
                disabled={actionLoading}
              > {translateLabel("txt_business_reject", "Reject")} </button>
            </div>
          </div>

          <div className="complaint-detail-side">
            <section className="complaint-receipt-card">
              <h3>{selectedComplaint.vendorName}</h3>
              <p>{selectedComplaint.pickupAddress}<br />{selectedComplaint.vendorPhone}</p>
              <div className="complaint-receipt-line" />
              <strong>{translateLabel("txt_end_user_order_receipt", "Order Receipt")}</strong>
              <dl>
                <div><dt>{translateLabel("txt_end_user_order_id", "Order ID:")}</dt><dd>{selectedComplaint.orderId}</dd></div>
                <div><dt>{translateLabel("txt_admin_order_date", "Order Date:")}</dt><dd>{selectedComplaint.created}</dd></div>
                <div><dt>{translateLabel("txt_common_customer", "Customer:")}</dt><dd>{selectedComplaint.name}</dd></div>
                <div><dt>{translateLabel("txt_end_user_total", "Total:")}</dt><dd>{selectedComplaint.amount}</dd></div>
                <div><dt>{translateLabel("txt_common_payment_status", "Payment Status:")}</dt><dd>{translateLabel("input_addons_paid", "Paid")}</dd></div>
              </dl>
              <div className="complaint-receipt-line" />
              <b>{translateLabel("txt_admin_thank_you_for_your_order", "Thank you for your order!")}</b>
            </section>

            <section className="complaint-side-card">
              <div className="complaint-side-card-title">
                <strong>{translateLabel("txt_admin_adjustment_summary", "Adjustment Summary")}</strong>
                <FiMoreHorizontal />
              </div>
              <dl>
                <div><dt>{translateLabel("txt_admin_recipient", "Recipient")}</dt><dd>{selectedComplaint.name}</dd></div>
                <div><dt>{translateLabel("txt_admin_reason", "Reason")}</dt><dd>{selectedComplaint.reason}</dd></div>
                <div><dt>{translateLabel("txt_admin_submitted_on", "Submitted on")}</dt><dd>{selectedComplaint.submittedOn}</dd></div>
                <div><dt>{translateLabel("txt_admin_total_refund", "Total Refund")}</dt><dd className="is-orange">{selectedComplaint.amount}</dd></div>
              </dl>
            </section>

            <section className="complaint-side-card">
              <div className="complaint-side-card-title">
                <strong>{translateLabel("txt_admin_activity_log", "Activity Log")}</strong>
                <FiMoreHorizontal />
              </div>
              <div className="complaint-activity">
                {["Request submitted", "Reviewed", "Voucher created"].map(
                  (item, index) => (
                    <div className="complaint-activity-item" key={item}>
                      <span />
                      <div>
                        <strong>{item}</strong>
                        <small>By Nagham</small>
                      </div>
                      <time>May 22, 2026 - 10:{18 + index * 14} AM</time>
                    </div>
                  ),
                )}
              </div>
            </section>
          </div>
        </div>

        {complaintModal === "approved" && (
          <div className="complaint-action-modal-backdrop">
            <div className="complaint-action-modal complaint-action-modal-approved">
              <button
                type="button"
                className="complaint-action-modal-close"
                onClick={() => setComplaintModal(null)}
              >
                x
              </button>
              <ComplaintApprovedIcon />
              <h2>{translateLabel("txt_admin_complain_approved", "Complain Approved")}</h2>
            </div>
          </div>
        )}

        {complaintModal === "rejected" && (
          <div className="complaint-action-modal-backdrop">
            <div className="complaint-action-modal complaint-action-modal-rejected">
              <button
                type="button"
                className="complaint-action-modal-close"
                onClick={() => setComplaintModal(null)}
              >
                x
              </button>
              <ComplaintRejectedIcon />
              <h2>{translateLabel("txt_admin_complain_rejection", "Complain Rejection")}</h2>
              <p>{translateLabel("txt_admin_please_provide_a_reason_for_rejection", "Please provide a reason for rejection.")}</p>
              <label className="complaint-rejection-field">
                <span>{translateLabel("txt_admin_rejection_reason", "Rejection Reason")}</span>
                <textarea
                  value={rejectionReason}
                  onChange={(event) => setRejectionReason(event.target.value)}
                  placeholder={translateLabel("txt_admin_explain_why_this_complain_is_being_rejected", "Explain why this complain is being rejected...")}
                />
              </label>
              {actionError && (
                <div className="complaint-modal-error">{actionError}</div>
              )}
              <div className="complaint-rejection-actions">
                <button
                  type="button"
                  className="modal-btn-cancel"
                  onClick={() => setComplaintModal(null)}
                > {translateLabel("btn_cancel", "Cancel")} </button>
                <button
                  type="button"
                  className="modal-btn-reject"
                  onClick={rejectComplaint}
                  disabled={actionLoading || !rejectionReason.trim()}
                >
                  {actionLoading ? translateLabel("btn_submitting", "Submitting...") : translateLabel("txt_admin_submit", "Submit")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="complaint-center-page">
      <div className="complaint-center-header">
        <div className="complaint-center-heading">
          <h1>{translateLabel("txt_admin_complaint_issue_center", "Complaint & Issue Center")}</h1>
          <p>{translateLabel("txt_admin_manage_issues_related_to_discovery_browsing_and_fulfillment", "Manage issues related to discovery, browsing, and fulfillment.")}</p>
        </div>

        <div className="complaint-center-actions">
          <button type="button" onClick={clearFilters}> {translateLabel("txt_admin_clear_all", "Clear All")} </button>
          <button type="button" onClick={applyFilters}> {translateLabel("txt_admin_apply_all", "Apply All")} </button>
        </div>
      </div>

      <div className="complaint-center-filters">
        <label className="complaint-center-search">
          <FiSearch size={18} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={translateLabel("txt_admin_search_by_complaint_id_customer_name_order_id_or_phone_number", "Search by complaint ID, customer name, order ID or phone number...")}
          />
        </label>

        <div className="complaint-center-date-wrap">
          <button
            type="button"
            className="complaint-center-date"
            onClick={() => {
              setDraftRange(dateRange);
              setIsDateOpen((current) => !current);
            }}
          >
            <span>{formatRangeLabel(dateRange)}</span>
            <FiChevronDown size={16} />
          </button>

          {isDateOpen && (
            <div className="complaint-date-popover">
              <div className="complaint-date-title">{translateLabel("txt_web_vendor_filter", "Filter")}</div>
              <div className="complaint-date-line" />

              <div className="complaint-date-fields">
                <div>
                  <label>{translateLabel("txt_admin_from", "From")}</label>
                  <div className="complaint-date-field">
                    <span>{formatDateInput(draftRange.from)}</span>
                    <FiCalendar size={14} />
                  </div>
                </div>
                <div>
                  <label>{translateLabel("txt_time_to", "To")}</label>
                  <div className="complaint-date-field">
                    <span>{formatDateInput(draftRange.to)}</span>
                    <FiCalendar size={14} />
                  </div>
                </div>
              </div>

              <div className="complaint-calendar">
                <div className="complaint-calendar-month">
                  <button type="button" onClick={() => moveMonth(-1)}>
                    <FiChevronLeft className="navigation-direction-icon" size={15} />
                  </button>
                  <span>{visibleMonthLabel}</span>
                  <button type="button" onClick={() => moveMonth(1)}>
                    <FiChevronRight className="navigation-direction-icon" size={15} />
                  </button>
                </div>

                <div className="complaint-calendar-grid">
                  {WEEKDAYS.map((day) => (
                    <div className="complaint-calendar-weekday" key={day}>
                      {day}
                    </div>
                  ))}
                  {calendarCells.map(({ date, outside }) => {
                    const selected =
                      sameDay(date, draftRange.from) ||
                      sameDay(date, draftRange.to);
                    const inRange = isBetweenDates(
                      new Date(date),
                      draftRange.from,
                      draftRange.to,
                    );

                    return (
                      <button
                        type="button"
                        key={date.toISOString()}
                        className={[
                          "complaint-calendar-day",
                          outside ? "outside" : "",
                          selected ? "selected" : "",
                          inRange ? "in-range" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        onClick={() => selectDate(date)}
                      >
                        {date.getDate()}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="complaint-date-actions">
                <button type="button" onClick={clearDateRange}> {translateLabel("txt_admin_clear_all", "Clear All")} </button>
                <button type="button" onClick={applyDateRange}> {translateLabel("txt_admin_apply_all", "Apply All")} </button>
              </div>
            </div>
          )}
        </div>

        <div className="complaint-center-status">
          <button
            type="button"
            onClick={() => setIsStatusOpen((current) => !current)}
          >
            <span>{status}</span>
            <FiChevronDown size={16} />
          </button>

          {isStatusOpen && (
            <div className="complaint-center-status-menu">
              {STATUS_OPTIONS.map((option) => (
                <div
                  key={option}
                  className={option === status ? "is-selected" : ""}
                  onClick={() => {
                    setStatus(option);
                    setIsStatusOpen(false);
                  }}
                >
                  {option}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="complaint-center-table" role="table">
        <div className="complaint-center-row complaint-center-row-head" role="row">
          <div>{translateLabel("txt_admin_complaint_id", "Complaint ID")}</div>
          <div>{translateLabel("txt_admin_name", "Name")}</div>
          <div>{translateLabel("txt_end_user_order_id", "Order ID")}</div>
          <div>{translateLabel("txt_admin_amount", "Amount")}</div>
          <div>{translateLabel("txt_admin_reason", "Reason")}</div>
          <div>{translateLabel("txt_common_status", "Status")}</div>
          <div>{translateLabel("txt_admin_created", "Created")}</div>
          <div>{translateLabel("txt_common_actions", "Actions")}</div>
        </div>

        {complaintsLoading && (
          <div className="complaint-center-empty">Loading complaints...</div>
        )}

        {!complaintsLoading && complaintsError && (
          <div className="complaint-center-empty complaint-center-error">
            {complaintsError}
          </div>
        )}

        {!complaintsLoading && !complaintsError && filteredComplaints.map((complaint, index) => (
          <div className="complaint-center-row" role="row" key={`${complaint.id}-${index}`}>
            <div className="complaint-center-id">{complaint.id}</div>
            <div className="complaint-center-customer">
              <span>{complaint.name}</span>
              <small>{complaint.phone}</small>
            </div>
            <div className="complaint-center-order">{complaint.orderId}</div>
            <div className="complaint-center-amount">{complaint.amount}</div>
            <div className="complaint-center-reason">{complaint.reason}</div>
            <div>
              <span
                className={`complaint-center-badge complaint-center-badge-${complaint.status.toLowerCase()}`}
              >
                {complaint.status}
              </span>
            </div>
            <div className="complaint-center-created">{complaint.created}</div>
            <div>
              <button
                type="button"
                className="complaint-center-view"
                onClick={() => openComplaintDetails(complaint)}
                disabled={detailsLoadingId === complaint.complaintId}
              >
                {detailsLoadingId === complaint.complaintId ? "..." : <FiEye size={15} />}
              </button>
            </div>
          </div>
        ))}

        {!complaintsLoading && !complaintsError && filteredComplaints.length === 0 && (
          <div className="complaint-center-empty">No complaints found.</div>
        )}
      </div>
    </div>
  );
}
